import { useMemo } from "react";
import { Box, Skeleton, VStack } from "@chakra-ui/react";
import { NewHireJourney } from "models/v2/newHireJourney";
import { createQueryString } from "helpers/queryString";
import { useJourneyAttentionStatusesQuery, useNewHiresListQuery } from "features/NewHireJourney/hooks";
import { useCurrentUserQuery } from "features/User/hooks";
import NewHireJourneyListRow from "./NewHireJourneyListRow";

interface NewHireJourneysListProps {
  additionalQueries?: Record<string, string | string[]>;
  emptyStateComponent?: React.ReactNode;
  onSelectJourney?: (journey: NewHireJourney) => void;
}

export default function NewHireJourneysList({
  additionalQueries,
  emptyStateComponent,
  onSelectJourney,
}: NewHireJourneysListProps) {
  const queryString = useMemo(() => createQueryString({ additionalQueries }), [additionalQueries]);
  const { data, isLoading } = useNewHiresListQuery(queryString);
  const { data: currentUser } = useCurrentUserQuery();
  const newHireJourneys = useMemo(() => (data ?? []) as NewHireJourney[], [data]);

  const visibleIds = useMemo(() => newHireJourneys.map((j) => j.id), [newHireJourneys]);
  const { data: attentionByJourneyId } = useJourneyAttentionStatusesQuery(visibleIds);

  if (isLoading) {
    return (
      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
        <VStack spacing={0} align="stretch">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} height="64px" />
          ))}
        </VStack>
      </Box>
    );
  }

  if (newHireJourneys.length === 0 && emptyStateComponent) {
    return <>{emptyStateComponent}</>;
  }

  return (
    <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="lg" overflow="hidden">
      <VStack spacing={0} align="stretch">
        {newHireJourneys.map((journey: NewHireJourney) => (
          <NewHireJourneyListRow
            key={journey.id}
            journey={journey}
            currentUser={currentUser}
            attention={attentionByJourneyId?.[journey.id]}
            onSelect={onSelectJourney}
          />
        ))}
      </VStack>
    </Box>
  );
}
